import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import NotificationSnackbar from '../../../components/notificationSnackbar';
import { formFieldSx, helperTextSx } from '../../../styles/formStyles';
import { resultMessage } from '../../../../service/apiResult';
import {
    getInitiativeRedirects,
    getIntegratorTypes,
    updateInitiativeRedirects,
    updateIntegratorTypeAppUrl,
} from './integratorRedirects.service';

const URL_PREFIX_ERROR = 'Enter a full URL starting with http:// or https://';

const cardSx = {
    p: 4,
    borderRadius: 4,
    border: '1px solid #E7ECF2',
    backgroundColor: '#FFFFFF',
    maxWidth: 980,
};

const sectionTitleSx = { fontSize: 22, fontWeight: 700, color: '#202124', mb: 0.5 };
const sectionTextSx = { color: '#6B7280', maxWidth: 820, mb: 2.5 };

function toSentenceCase(value) {
    if (!value) return '';
    return value
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());
}

function isValidUrl(value) {
    return value.startsWith('http://') || value.startsWith('https://');
}

function StatusChip({ configured }) {
    return (
        <Chip
            label={configured ? 'Configured' : 'Not configured'}
            size="small"
            sx={{
                fontWeight: 600,
                color: configured ? '#1B5E20' : '#8A6D00',
                backgroundColor: configured ? '#E6F4EA' : '#FFF4D6',
            }}
        />
    );
}

/**
 * Settings screen for where eK Link sends an Ecosystem Integrator after OTP verification.
 *
 * Initiatives: every Initiative is listed (a new one shows up on its own) with one URL for its open
 * and one for its closed Initiative Based integrators, so the URL is set once per Initiative instead
 * of on every integrator. Integrator types: the URL per type, the fallback when nothing more specific
 * is set. A type left blank has no destination: its integrators are refused at the eK Link login
 * screen rather than dropped somewhere wrong.
 *
 * Order for an Initiative Based integrator: its own URL (Ecosystem Integrators, for exceptions), then its
 * Initiative's open or closed URL, then the Initiative Based type URL.
 */
function IntegratorRedirects() {
    const [types, setTypes] = useState([]);
    const [drafts, setDrafts] = useState({});
    const [errors, setErrors] = useState({});
    const [savingId, setSavingId] = useState(null);
    const [initiatives, setInitiatives] = useState([]);
    const [initiativeDrafts, setInitiativeDrafts] = useState({});
    const [initiativeErrors, setInitiativeErrors] = useState({});
    const [savingInitiativeId, setSavingInitiativeId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const notify = (message, severity) => setSnackbar({ open: true, message, severity });
    const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

    const applyTypes = useCallback((list) => {
        setTypes(list);
        setDrafts(list.reduce((acc, type) => ({ ...acc, [type.id]: type.appUrl || '' }), {}));
        setErrors({});
    }, []);

    const draftOf = (item) => ({ open: item.openAppUrl || '', closed: item.closedAppUrl || '' });

    const applyInitiatives = useCallback((list) => {
        setInitiatives(list);
        setInitiativeDrafts(list.reduce((acc, item) => ({ ...acc, [item.initiativeId]: draftOf(item) }), {}));
        setInitiativeErrors({});
    }, []);

    useEffect(() => {
        let active = true;
        (async () => {
            const [typesResult, initiativesResult] = await Promise.all([getIntegratorTypes(), getInitiativeRedirects()]);
            if (!active) return;
            if (typesResult.success && Array.isArray(typesResult.data)) {
                applyTypes(typesResult.data);
            } else {
                notify(resultMessage(typesResult, 'Failed to load integrator types'), 'error');
            }
            if (initiativesResult.success && Array.isArray(initiativesResult.data)) {
                applyInitiatives(initiativesResult.data);
            } else {
                notify(resultMessage(initiativesResult, 'Failed to load Initiatives'), 'error');
            }
            setLoading(false);
        })();
        return () => {
            active = false;
        };
    }, [applyTypes, applyInitiatives]);

    const initiativeTypeUrl = types.find((type) => type.category === 'INITIATIVE_BASED')?.appUrl || '';

    const handleChange = (typeId, value) => {
        setDrafts((prev) => ({ ...prev, [typeId]: value }));
        setErrors((prev) => ({ ...prev, [typeId]: '' }));
    };

    const handleSave = async (type) => {
        const value = (drafts[type.id] ?? '').trim();

        // Mirrors the server-side rule: a relative path would send the integrator back into eK Link.
        if (value && !isValidUrl(value)) {
            setErrors((prev) => ({ ...prev, [type.id]: URL_PREFIX_ERROR }));
            return;
        }

        setSavingId(type.id);
        const result = await updateIntegratorTypeAppUrl(type.id, value || null);
        setSavingId(null);

        if (!result.success) {
            notify(resultMessage(result, 'Failed to update redirect URL'), 'error');
            return;
        }

        const updated = result.data;
        setTypes((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
        setDrafts((prev) => ({ ...prev, [updated.id]: updated.appUrl || '' }));
        notify(
            updated.appUrl
                ? `${type.name} redirect URL saved`
                : `${type.name} redirect URL cleared — integrators of this type can no longer log in unless a more specific URL applies`,
            'success'
        );
    };

    const isDirty = (type) => (drafts[type.id] ?? '').trim() !== (type.appUrl || '');

    const handleInitiativeChange = (initiativeId, field, value) => {
        setInitiativeDrafts((prev) => ({ ...prev, [initiativeId]: { ...prev[initiativeId], [field]: value } }));
        setInitiativeErrors((prev) => ({ ...prev, [initiativeId]: { ...prev[initiativeId], [field]: '' } }));
    };

    const isInitiativeDirty = (item) => {
        const draft = initiativeDrafts[item.initiativeId] || draftOf(item);
        return draft.open.trim() !== (item.openAppUrl || '') || draft.closed.trim() !== (item.closedAppUrl || '');
    };

    const handleInitiativeSave = async (item) => {
        const draft = initiativeDrafts[item.initiativeId] || draftOf(item);
        const open = draft.open.trim();
        const closed = draft.closed.trim();
        const fieldErrors = {
            open: open && !isValidUrl(open) ? URL_PREFIX_ERROR : '',
            closed: closed && !isValidUrl(closed) ? URL_PREFIX_ERROR : '',
        };
        if (fieldErrors.open || fieldErrors.closed) {
            setInitiativeErrors((prev) => ({ ...prev, [item.initiativeId]: fieldErrors }));
            return;
        }

        setSavingInitiativeId(item.initiativeId);
        const result = await updateInitiativeRedirects(item.initiativeId, open || null, closed || null);
        setSavingInitiativeId(null);

        if (!result.success) {
            notify(resultMessage(result, 'Failed to update Initiative redirect URLs'), 'error');
            return;
        }

        const updated = result.data;
        setInitiatives((prev) => prev.map((entry) => (entry.initiativeId === updated.initiativeId ? updated : entry)));
        setInitiativeDrafts((prev) => ({ ...prev, [updated.initiativeId]: draftOf(updated) }));
        notify(`${item.name} redirect URLs saved`, 'success');
    };

    const fallbackHelper = (count, kind) => {
        const who = count === 1 ? `the ${kind} integrator` : `${kind} integrators`;
        return initiativeTypeUrl
            ? `Empty: ${who} use the Initiative Based URL below (${initiativeTypeUrl}).`
            : `Empty: ${who} are refused at eK Link login until a URL is set here or below.`;
    };

    const renderInitiative = (item) => {
        const draft = initiativeDrafts[item.initiativeId] || draftOf(item);
        const fieldErrors = initiativeErrors[item.initiativeId] || {};
        const saving = savingInitiativeId === item.initiativeId;
        const configured = !!(item.openAppUrl || item.closedAppUrl);

        return (
            <Paper key={item.initiativeId} elevation={0} sx={cardSx}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#202124' }}>{item.name}</Typography>
                    <StatusChip configured={configured} />
                    {item.initiativeType === 'CREATOR' && <Chip label="Creator Initiative" size="small" variant="outlined" />}
                    {!item.active && <Chip label="Inactive" size="small" variant="outlined" />}
                </Box>
                <Typography sx={{ color: '#6B7280', mb: 3 }}>
                    {(item.countries || []).length > 0 ? item.countries.join(', ') : 'No country'}
                    {' · '}
                    {item.openIntegrators} open and {item.closedIntegrators} closed Initiative Based integrator
                    {item.openIntegrators + item.closedIntegrators === 1 ? '' : 's'}
                </Typography>

                <Divider sx={{ borderColor: '#EEF2F7', mb: 3 }} />

                <Grid container spacing={2.5}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            fullWidth
                            label="Open ecosystem integrators"
                            placeholder="https://open-app.example.com/login"
                            value={draft.open}
                            onChange={(event) => handleInitiativeChange(item.initiativeId, 'open', event.target.value)}
                            error={!!fieldErrors.open}
                            helperText={fieldErrors.open || (draft.open.trim() ? ' ' : fallbackHelper(item.openIntegrators, 'open'))}
                            disabled={saving}
                            sx={{ ...formFieldSx, ...helperTextSx }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            fullWidth
                            label="Closed ecosystem integrators"
                            placeholder="https://closed-app.example.com/login"
                            value={draft.closed}
                            onChange={(event) => handleInitiativeChange(item.initiativeId, 'closed', event.target.value)}
                            error={!!fieldErrors.closed}
                            helperText={fieldErrors.closed || (draft.closed.trim() ? ' ' : fallbackHelper(item.closedIntegrators, 'closed'))}
                            disabled={saving}
                            sx={{ ...formFieldSx, ...helperTextSx }}
                        />
                    </Grid>
                </Grid>

                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 2.5 }}>
                    <Button
                        variant="contained"
                        onClick={() => handleInitiativeSave(item)}
                        disabled={saving || !isInitiativeDirty(item)}
                        sx={{ textTransform: 'none', borderRadius: '10px', minWidth: 120 }}
                    >
                        Save
                    </Button>
                    {saving && <CircularProgress size={18} />}
                </Stack>
            </Paper>
        );
    };

    const renderType = (type) => (
        <Paper key={type.id} elevation={0} sx={cardSx}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#202124' }}>
                    {type.name || toSentenceCase(type.category)}
                </Typography>
                <StatusChip configured={!!type.appUrl} />
            </Box>
            <Typography sx={{ color: '#6B7280', mb: 3 }}>
                {type.category === 'INITIATIVE_BASED'
                    ? 'Fallback for Initiative Based integrators whose Initiative above has no URL for them and who have no URL of their own.'
                    : type.description || `Integrators on ${toSentenceCase(type.category)} ecosystems.`}
            </Typography>

            <Divider sx={{ borderColor: '#EEF2F7', mb: 3 }} />

            <TextField
                fullWidth
                label="Redirect URL"
                placeholder="https://app.example.com/login"
                value={drafts[type.id] ?? ''}
                onChange={(event) => handleChange(type.id, event.target.value)}
                error={!!errors[type.id]}
                helperText={
                    errors[type.id] ||
                    (type.appUrl
                        ? 'Leave empty and save to stop integrators of this type from logging in.'
                        : 'Until a URL is set, integrators of this type are refused at the eK Link login screen.')
                }
                disabled={savingId === type.id}
                sx={{ ...formFieldSx, ...helperTextSx }}
            />

            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 2.5 }}>
                <Button
                    variant="contained"
                    onClick={() => handleSave(type)}
                    disabled={savingId === type.id || !isDirty(type)}
                    sx={{ textTransform: 'none', borderRadius: '10px', minWidth: 120 }}
                >
                    Save
                </Button>
                {savingId === type.id && <CircularProgress size={18} />}
            </Stack>
        </Paper>
    );

    return (
        <Box>
            <Typography className="app-page-title" sx={{ fontSize: 30, fontWeight: 700, color: '#202124', mb: 1 }}>
                Redirect Configuration
            </Typography>
            <Typography sx={{ color: '#6B7280', maxWidth: 820, mb: 4 }}>
                Where eK Link sends an Ecosystem Integrator after they verify their OTP. Set it once per
                Initiative for its open and closed integrators, and once per integrator type as the
                fallback. An integrator with its own URL (set on the integrator in Ecosystem Integrators
                management) uses that instead.
            </Typography>

            {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
                    <CircularProgress size={20} />
                    <Typography sx={{ color: '#6B7280' }}>Loading redirect configuration&hellip;</Typography>
                </Box>
            ) : (
                <Stack spacing={5}>
                    <Box>
                        <Typography sx={sectionTitleSx}>Initiatives</Typography>
                        <Typography sx={sectionTextSx}>
                            Each Initiative Based integrator is sent to its Initiative&apos;s URL for open or
                            closed integrators. New Initiatives appear here automatically.
                        </Typography>
                        {initiatives.length > 0 ? (
                            <Stack spacing={3}>{initiatives.map(renderInitiative)}</Stack>
                        ) : (
                            <Typography sx={{ color: '#6B7280' }}>
                                No Initiatives yet. Create one under Initiatives and it will appear here.
                            </Typography>
                        )}
                    </Box>

                    <Box>
                        <Typography sx={sectionTitleSx}>Integrator types</Typography>
                        <Typography sx={sectionTextSx}>
                            One application URL per integrator type, used when nothing more specific is set.
                        </Typography>
                        <Stack spacing={3}>{types.map(renderType)}</Stack>
                    </Box>
                </Stack>
            )}

            <NotificationSnackbar
                open={snackbar.open}
                onClose={closeSnackbar}
                message={snackbar.message}
                severity={snackbar.severity}
            />
        </Box>
    );
}

export default IntegratorRedirects;
